import { create } from 'zustand';
import { generateId } from '@/utils/misc';
import type { DownloadRecord } from '@/types';

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: 'success' | 'error' | 'info';
}

interface UiState {
  toasts: ToastItem[];
  pushToast: (toast: Omit<ToastItem, 'id'>) => void;
  dismissToast: (id: string) => void;
  /** Drives the "Preparing your media…" overlay. */
  processingActive: boolean;
  setProcessingActive: (active: boolean) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;
  /** "Your media is ready" modal shown the instant a job completes. */
  downloadReadyOpen: boolean;
  readyRecord: DownloadRecord | null;
  openDownloadReady: (record: DownloadRecord) => void;
  closeDownloadReady: () => void;
  /** Ad interstitial shown before a signed-in user's final download. */
  adGateOpen: boolean;
  adGate: { url: string; title: string } | null;
  openAdGate: (payload: { url: string; title: string }) => void;
  closeAdGate: () => void;
}

const TOAST_TTL_MS = 4500;

export const useUiStore = create<UiState>((set) => ({
  toasts: [],
  pushToast: (toast) => {
    const id = generateId();
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }].slice(-4) }));
    window.setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) }));
    }, TOAST_TTL_MS);
  },
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) })),
  processingActive: false,
  setProcessingActive: (processingActive) => set({ processingActive }),
  shortcutsOpen: false,
  setShortcutsOpen: (shortcutsOpen) => set({ shortcutsOpen }),
  mobileSidebarOpen: false,
  setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),
  downloadReadyOpen: false,
  readyRecord: null,
  openDownloadReady: (record) => set({ readyRecord: record, downloadReadyOpen: true }),
  // Keep readyRecord so the modal's content stays visible during the exit anim.
  closeDownloadReady: () => set({ downloadReadyOpen: false }),
  adGateOpen: false,
  adGate: null,
  openAdGate: (adGate) => set({ adGate, adGateOpen: true }),
  // Keep adGate for the exit anim; overwritten on next open.
  closeAdGate: () => set({ adGateOpen: false }),
}));

/** Imperative toast helper usable outside React components. */
export function toast(item: Omit<ToastItem, 'id'>): void {
  useUiStore.getState().pushToast(item);
}
